import React, { useEffect, useMemo, useState } from "react";
import jwt from "jwt-decode";
import user_service from "../service/user_service";
import Loader from "../../Pages/Loader/Loader.js";
import Toaster from "../../Pages/Toaster/Toaster.js";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "unapproved", label: "Unapproved" },
];

function safeDecodeAuth() {
  const token = localStorage.getItem("auth");
  if (!token) return null;
  try {
    return jwt(token);
  } catch {
    return null;
  }
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString();
}

const AdminVendorAds = () => {
  const [loader, setLoader] = useState({ isActive: null });
  const { isActive } = loader;
  const [toaster, setToaster] = useState({
    types: null,
    isShow: null,
    toasterBody: "",
    message: "",
  });
  const { types, isShow, toasterBody, message } = toaster;

  const decoded = useMemo(() => safeDecodeAuth(), []);
  const isAdmin =
    decoded?.contactType === "admin" || decoded?.contactType === "staff";

  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);

  const load = async (selectedStatus = status) => {
    setLoader({ isActive: true });
    try {
      const resp = await user_service.vendorAdsAdminQueue(selectedStatus);
      const data = resp?.data?.data || [];
      setItems(data);
      setLoader({ isActive: false });
    } catch (e) {
      setLoader({ isActive: false });
      setToaster({
        types: "error",
        isShow: true,
        toasterBody: "Unable to load vendor ads queue.",
        message: e?.message || "",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 2500);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    load(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const approve = async (id) => {
    setLoader({ isActive: true });
    try {
      await user_service.vendorAdsAdminApprove(id);
      setLoader({ isActive: false });
      setToaster({
        types: "success",
        isShow: true,
        toasterBody: "Vendor ad approved.",
        message: "",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 2000);
      await load(status);
    } catch (e) {
      setLoader({ isActive: false });
      setToaster({
        types: "error",
        isShow: true,
        toasterBody: "Approval failed.",
        message: e?.response?.data?.message || e?.message || "",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 2500);
    }
  };

  const reject = async (id) => {
    setLoader({ isActive: true });
    try {
      await user_service.vendorAdsAdminReject(id);
      setLoader({ isActive: false });
      setToaster({
        types: "success",
        isShow: true,
        toasterBody: "Vendor ad rejected.",
        message: "",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 2000);
      await load(status);
    } catch (e) {
      setLoader({ isActive: false });
      setToaster({
        types: "error",
        isShow: true,
        toasterBody: "Rejection failed.",
        message: e?.response?.data?.message || e?.message || "",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 2500);
    }
  };

  if (!isAdmin) {
    return (
      <div className="bg-secondary float-left w-100 pt-4 mb-4">
        <main className="page-wrapper profile_page_wrap">
          <div className="content-overlay">
            <div className="container">
              <div className="alert alert-warning mt-4">
                You do not have access to this page.
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="bg-secondary float-left w-100 pt-4 mb-4">
      <Loader isActive={isActive} />
      {isShow && (
        <Toaster
          types={types}
          isShow={isShow}
          toasterBody={toasterBody}
          message={message}
        />
      )}
      <main className="page-wrapper profile_page_wrap">
        <div className="content-overlay">
          <div className="container">
            <div className="float-left w-100 d-flex align-items-center justify-content-between mb-3">
              <h3 className="text-white mb-0">Vendor Ads Approval</h3>
              <div className="d-flex align-items-center gap-2">
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <button
                  className="btn btn-outline-light"
                  type="button"
                  onClick={() => load(status)}
                >
                  Refresh
                </button>
              </div>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-body">
                {items?.length ? (
                  <div className="table-responsive">
                    <table className="table table-striped align-middle">
                      <thead>
                        <tr>
                          <th>Company</th>
                          <th>Ad</th>
                          <th>Package</th>
                          <th>Payment</th>
                          <th>Requested</th>
                          <th>Expires</th>
                          <th style={{ width: 220 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((it) => (
                          <tr key={it._id}>
                            <td>{it.companyName || ""}</td>
                            <td>
                              <div className="fw-semibold">{it.productname}</div>
                              <div className="text-muted small">
                                {it.email || ""} {it.phone ? `• ${it.phone}` : ""}
                              </div>
                            </td>
                            <td>{it.vendorAdPackageMonths ? `${it.vendorAdPackageMonths} months` : ""}</td>
                            <td>{it.vendorAdPaymentStatus || ""}</td>
                            <td>{formatDate(it.vendorAdApprovalRequestedAt)}</td>
                            <td>{formatDate(it.vendorAdExpiresAt)}</td>
                            <td>
                              {status === "pending" ? (
                                <div className="d-flex gap-2">
                                  <button
                                    className="btn btn-sm btn-success"
                                    type="button"
                                    onClick={() => approve(it._id)}
                                  >
                                    Approve
                                  </button>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    type="button"
                                    onClick={() => reject(it._id)}
                                  >
                                    Reject
                                  </button>
                                </div>
                              ) : (
                                <span className="text-muted small">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-muted">No vendor ads found.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminVendorAds;

